import type { LayerEigenbewegung } from "./eigenbewegung.js";
// Post type definitions — extracted from app/src/types/post.ts (pre-Phase-12).
// Pure types + runtime enums + pure type-guard helpers. No external runtime deps.

import type { Vector3Tuple } from "three";
import type { PossibleEasings } from "./easings.js";
import type { TextContent } from "./layer-content.js";

/**
 * Font information DTO — inlined from scrollyfrontend server actions
 */
export interface FontInfoDto {
    familyName: string;
    weight: number;
    italic: boolean;
    variable: boolean;
    filename: string;
    fontUrl: string;
}

/**
 * Tenant handling in our new system is still undefined. The template JSON does include them, but we should not use or rely on them.
 */
enum RealTenantId {
    SCRLLY = "scrlly",
    CORPORATE = "corporate",
    FIVETIMES = "five-times",
    MERCEDES = "mercedes",
    TEST = "test",
    ZEIT = "zeit",
    DVAG = "dvag",
    TWENTYR = "20r",
    SEACLOUD = "seacloud",
    DISCOVERSEACLOUD = "discoverseacloud",
    GREENTECH = "greentech",
    PLAYGROUND = "playground",
    MEDIAIMPACT = "mediaimpact",
    HANDELSZEITUNG = "handelszeitung",
    BEOBACHTER = "beobachter",
    SPARKASSE = "sparkasse",
    VERTICAL52 = "vertical52",
    NZZ = "nzz",
    HORNBACH = "hornbach",
    MML = "mml",
    LEMON = "lemon",
    TRACK = "track",
    HITCHON = "hitchon",
    PRESTEL = "prestel",
    WIWO = "wiwo",
    SPECTARIS = "spectaris",
    EOMUC = "eomuc",
    STEPSTONE = "stepstone",
    SCROLLYAI = "scrollyai",
    BOESCH = "boesch",
    VIENNA_AT = "vienna-at",
    AOK = "aok",
    GARCIAMEDIA = "garciamedia",
    BILD = "bild",
    FOCUS = "focus",
    TUICRUISES = "tuicruises",
    DESIGNBESTSELLER = "design-bestseller",
    VORWERK = "vorwerk",
    ABENDBLATT = "abendblatt",
    EDUCATION = "education",
    DROEMER_KNAUR = "droemer-knaur",
    MJNT = "mjnt",
    EDELSTOFFMEDIA = "edelstoffmedia",
    HOWTO = "how-to",
    REGENBOGEN = "regenbogen",
    FOODBOOM = "foodboom",
    MIKA = "mika",
    BVB = "bvb",
    MKVISUAL = "mk-visual",
    VOLKSBANK = "volksbank",
    BESTOF = "best-of",
    LZMEDIEN = "lzmedien",
    PPI = "ppi",
    BIOLAND = "bioland",
}

export enum PostVisibility {
    /** Listed and viewable for all */
    public = "public",
    /** Listed for all, but viewable only for signed-in users */
    private = "private",
    /** Not listed, but viewable for all */
    hidden = "hidden",
}

export interface Color {
    r: number;
    g: number;
    b: number;
    a: number;
}

export interface PresentationOptionsSerialized {
    ambientIntensity: number;
    slideOptions: SlideOptionsSerialized;
    // Port "HDRI" (Image-Based-Lighting). Alle optional — fehlen bei Bestands-
    // Scrollys ohne HDRI, dann ist syncEnvironment ein No-op.
    /** URL der Equirect-HDRI (.hdr/.exr/.jpg/.png/.webp). Leer/fehlend = aus. */
    environmentHdri?: string;
    /** Dateiname zur Decoder-Wahl (.exr → EXR, .jpg/png/webp → LDR, sonst RGBE). */
    environmentHdriName?: string;
    /** envMapIntensity auf allen PBR-Materialien (Default 1). */
    environmentIntensity?: number;
    /** Statische Dome-/Reflexions-Rotation in Grad. */
    environmentRotation?: number;
    /** Optionaler Keyframe-Kanal, der die Rotation ueber den Scroll treibt. */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    environmentRotationAnim?: any;
    /** HDRI zusaetzlich als sichtbaren Skydome-Hintergrund zeigen. */
    environmentAsBackground?: boolean;
    /** Skydome-Zoom (Spread um die Kamera-Vorwaertsachse; Default 1). */
    environmentBackgroundScale?: number;
    /** Skydome-Belichtung/Helligkeit (Default 1). */
    environmentBackgroundBrightness?: number;
    /**
     * GRUNDFARBE DER SZENE (Jan 19.09.2026): die Farbe HINTER allen Ebenen.
     * Fehlt sie, gilt im Reader das bisherige Verhalten (Textbox-
     * Hintergrundfarbe, sonst Weiss). Vorher zeigte der Editor dort fest
     * Schwarz: eine halbtransparente unterste Ebene (Jans Hintergrundbild mit
     * 58 % Deckkraft) sah im Editor dunkelgrau und veroeffentlicht fast weiss
     * aus. Rangfolge + Aufloesung: `resolveSzenenGrundfarbe`.
     */
    sceneBackgroundColor?: { r: number; g: number; b: number; a?: number } | null;
    /**
     * MITLAUFENDER CTA (Jans Auftrag 05.08.2026): Fläche mit Handlungsaufruf,
     * die ab einer einstellbaren Slide unten am Bild erscheint und bis zum Ende
     * stehenbleibt.
     *
     * Hier und nicht in den Metadaten, weil er zur GESTALTUNG gehört und beide
     * Editoren `presentationOptions` ohnehin schreiben — kein zweites Feld,
     * keine Spiegelung. Was gezeichnet wird, entscheidet
     * `resolveRunningCta`/`runningCtaSichtbar` (running-cta.ts).
     *
     * Optional, weil Bestands-Scrollys und der Studio-Pfad ihn nicht kennen.
     */
    runningCta?: RunningCtaConfig | null;
    /**
     * MEHRERE CTAs (Jans Umbau 27.09.2026): Der Pro-Editor legt CTAs jetzt wie
     * Assets an („+ CTA"), jeder mit eigener Spanne von Slide X bis Slide Y.
     * `runningCta` oben bleibt lesbar (Altbestand, Template-Editor) — welche
     * Streifen es insgesamt gibt, sagt `alleRunningCtas` (running-cta.ts).
     */
    runningCtas?: RunningCtaConfig[] | null;
}

// Nur der TYP — zur Laufzeit bleibt davon nichts stehen, deshalb entsteht durch
// den Gegen-Import von `Color` in running-cta.ts kein Ringschluss.
import type { RunningCtaConfig } from "./running-cta.js";

/**
 * Schrift für Überschriften (h1–h3) und Fließtext der Text-Boxen.
 *
 * `weight` stand schon immer mit drin (meist 400), hatte im Viewer aber keine
 * Wirkung. Seit der Schriftwahl mit zwei Spalten (Jan 06.10.2026) gilt er,
 * wenn `schnittGewaehlt` gesetzt ist — nur dann, damit bestehende Scrollys
 * unverändert bleiben (Jans Entscheidung „Nur neu gewählte Schnitte“).
 */
export interface FontMappingEintrag {
    family: string;
    weight: number;
    schnittGewaehlt?: boolean;
}

export interface FontMapping {
    headline?: FontMappingEintrag;
    text?: FontMappingEintrag;
}

import type { CtaConfig } from "./cta.js";

export interface SlideOptionsSerialized {
    scrollable: boolean;
    safeAreaYPosition: number;
    safeAreaXPosition: number;
    backgroundColor: Color;
    fontColor: Color;
    fontInfos?: FontInfoDto[];
    fontMapping?: FontMapping;
    companyLogoPosition?: "left" | "center" | "none";
    companyLogoUrl?: string;
    padding: number;
    borderRadius: number;
    animationType: string;
    alignAtSafeArea: boolean;
    margin: number;
    companyBoxPosition: "left" | "center" | "right";
    /**
     * Feste Breite der HTML-Box, in Pixeln bei 3840 px Entwurfsbreite
     * (0 / fehlend = automatisch). Wird auf die Buehnenbreite umgerechnet, also
     * ein ANTEIL und keine echten Pixel.
     *
     * Wirkt nur in breiten Formaten: bei hochkant (Seitenverhaeltnis < 0,8)
     * geht die Box ohnehin ueber die volle Breite und der Wert wird
     * uebersprungen (Renderer.useMobile). Deshalb taugt er als Mittel gegen zu
     * kleine Video-Einbettungen in 4:3 und 1:1, ohne 9:16 zu verbiegen.
     */
    boxWidth?: number;
    /**
     * Schlagschatten und Unschaerfe der HTML-Box, wie der PRO-EDITOR sie pflegt
     * (Grundlagen → HTML-Boxen). Der Template-Editor setzt dieselben zwei
     * Effekte an der Slide (`blurBackground` / `boxShadow`); welcher Wert gilt,
     * entscheidet `resolveBoxEffects` (box-effects.ts).
     *
     * Optional, weil aeltere Scrollys und der Studio-Pfad sie nicht mitbringen.
     */
    boxShadow?: boolean | null;
    backdropBlur?: boolean | null;
    /**
     * Link-Design der HTML-Boxen (Farbe, besucht, Mouse-over, Unterstreichung),
     * s. link-style.ts. Fehlt = Textfarbe + Unterstrich.
     */
    linkStyle?: LinkStyle | null;
}

import type { LinkStyle } from "./link-style.js";

export interface SlideSerialized {
    from: number;
    name: string;
    id: string;
    topFleshColor: Color;
    bottomFleshColor: Color;
    text: {
        text: string;
        show?: boolean;
    };
    credits: {
        text: string;
        light: boolean;
        photographerUrl?: string;
        /** Bild-Ebene, zu der ein automatischer (Unsplash-)Nachweis gehört. */
        bildFuer?: string;
        /** Weitere Nachweise (zwei Bilder auf einer Slide, Jan 13.10.2026). */
        weitere?: Array<{ text: string; photographerUrl?: string; bildFuer?: string }>;
    };
    /**
     * Eigener Foto-Nachweis der TITEL-Slide (Startbildschirm). Jans Editor
     * pflegt ihn in einem separaten Feld, weil der Startbildschirm ein eigenes
     * Hintergrundbild hat; die Regel im Original lautet
     * `slide.from === 0 ? titleCredits : credits`. Das Feld fehlte hier bisher
     * ganz — deshalb kannte es nur der Editor und der Nachweis der Titel-Slide
     * blieb im Renderer unsichtbar (Jans Fund). Optional, weil ihn Alt-Posts
     * und der Studio-Pfad nicht mitbringen.
     */
    titleCredits?: {
        text: string;
        light: boolean;
        photographerUrl?: string;
        /** Bild-Ebene, zu der ein automatischer (Unsplash-)Nachweis gehört. */
        bildFuer?: string;
        /** Weitere Nachweise (zwei Bilder auf einer Slide, Jan 13.10.2026). */
        weitere?: Array<{ text: string; photographerUrl?: string; bildFuer?: string }>;
    };
    options: SlideOptionsSerialized;
    isChapter: boolean;
    /**
     * Name des Kapitels am Kapitel-Punkt im Leser (Pro-Editor → Slide-Menü
     * „Name am Punkt“, Jan 06.10.2026). Steht an der Slide, zu der der Punkt
     * springt (Kapitel-Slide; beim ersten Kapitel die erste Slide nach dem
     * Startbildschirm); fehlt/leer = „Kapitel 2“ bzw. „Chapter 2“.
     */
    chapterLabel?: string | null;
    /**
     * Call-to-Action-Button am unteren Rand der HTML-Box (Jans Auftrag
     * 05.08.2026). Fehlt er oder ist unvollstaendig, wird nichts gezeichnet —
     * die Regel steht in cta.ts (`resolveCta`), damit Editor und Leser nicht
     * unterschiedlich entscheiden.
     */
    cta?: CtaConfig | null;
    /** Glasmorphism: backdrop-filter blur on the text container */
    blurBackground?: boolean | null;
    /** Drop shadow on the text container */
    boxShadow?: boolean | null;
}

export enum LayerType {
    text = "text",
    svg = "svg",
    color = "color",
    image = "image",
    video = "video",
    lottie = "lottie",
    group = "group", // the content is an array of other layers
    object3d = "object3d",
    object3dChild = "object3dChild",
    light = "light",
    map = "map",
    infographic = "infographic",
    effect = "effect",
    // Pro-Editor-Audio-Layer (MP3 etc., unsichtbar): spielt im Lebenszeit-
    // Fenster wie ein Video — Editor via AudioRenderer, Reader via
    // packages/renderer AudioLayers (01.08.2026).
    audio = "audio",
}

export interface KeyframeSerialized {
    start: number;
    value: unknown;
    id?: string;
    easing: PossibleEasings;
}

export enum AnimationType {
    position = "position",
    rotation = "rotation",
    scale = "scale",
    opacity = "opacity",
    /**
     * Port 1 (Pro-Editor): skalarer Reveal-Kanal 0..1. Treibt den Aufbau von
     * `infographic`-Layern (Balken wachsen, Choropleth färbt sich ein).
     * Optional — Alt-Scrollys haben diese Gruppe nicht (siehe Guard in
     * packages/renderer/src/Player/Layer3D.ts).
     */
    progress = "progress",
    /**
     * Port 2 (Datenseite): die animierbaren Parameter der Partikel-Effekte.
     *
     * Hier steht der VERTRAG — was das Format kennt. Der Renderer wertet die
     * Kanaele noch NICHT aus; anders als bei `progress` gibt es dafuer kein
     * Feld in `Layer3D`. Das ist Absicht: Ein Feld ohne Konsumenten waere
     * toter Code, der so aussieht, als koennte er etwas.
     *
     * Die Trennung haelt die Messung ehrlich — die Delta-Sonde meldet danach
     * keine unbekannten Kanaele mehr (Daten-Delta geschlossen), aber weiterhin
     * `effect` als nicht gerendert (Renderer-Delta offen).
     */
    effectSize = "effectSize",
    effectSpeed = "effectSpeed",
    effectWind = "effectWind",
    effectDepth = "effectDepth",
    effectCount = "effectCount",
    effectEdgeFalloff = "effectEdgeFalloff",

    /**
     * Die uebrigen Kanaele aus Jans Editor — ebenfalls nur der Vertrag.
     *
     * Gemessen an fuenf echten Exporten: Sein Editor schreibt die VOLLE
     * Kanalliste auf JEDEN Layer, unabhaengig vom Typ. Ein Bild traegt also
     * `letterSpacing`, ein Text `displacementScale`. Das ist kein Fehler,
     * sondern seine einheitliche Struktur — der Renderer nimmt sich, was er
     * fuer den jeweiligen Typ braucht.
     *
     * Bild-Nachbearbeitung (Unschaerfe, Maske, Verschiebung):
     */
    blur = "blur",
    mask = "mask",
    displacementScale = "displacementScale",

    /** Schlagschatten: */
    shadowBlur = "shadowBlur",
    shadowOpacity = "shadowOpacity",
    shadowOffsetX = "shadowOffsetX",
    shadowOffsetY = "shadowOffsetY",

    /** Textsatz: */
    letterSpacing = "letterSpacing",
    lineHeight = "lineHeight",

    /**
     * Drehung der HDRI-Umgebung. Sitzt anders als die uebrigen NICHT auf einem
     * Layer, sondern auf der Praesentation (`presentationOptions`) — in den
     * Beispiel-Exporten taucht er deshalb nicht in `animationsGroups` auf.
     */
    environmentRotation = "environmentRotation",

    /**
     * Port "Anim-Kanaele": animierbare LICHT-Einstellungen. Skalare: intensity
     * (alle Lichter), lightDistance & lightDecay (point/spot), lightAngle &
     * lightPenumbra (spot). Vektoren: lightTarget (Zielpunkt, spot/directional),
     * lightColor ([r,g,b] 0..255). Der Renderer wendet sie pro Frame im
     * light-Zweig an; ohne aktive Gruppe greift der statische content-Wert.
     */
    intensity = "intensity",
    lightDistance = "lightDistance",
    lightDecay = "lightDecay",
    lightAngle = "lightAngle",
    lightPenumbra = "lightPenumbra",
    lightTarget = "lightTarget",
    lightColor = "lightColor",

    /**
     * Port "Anim-Kanaele": planare-Spiegel-Einstellungen (Mirrors Teil B) — die
     * animierten Werte ueberschreiben den statischen mirrorStrength/mirrorBlur
     * nur waehrend der Animation.
     */
    mirrorStrength = "mirrorStrength",
    mirrorBlur = "mirrorBlur",

    /**
     * Port "Anim-Kanaele": star-Primitive — Aussen-/Innen-Radius + Spitzenzahl,
     * animierbar (der Renderer baut die Geometrie pro Frame neu).
     */
    starOuterRadius = "starOuterRadius",
    starInnerRadius = "starInnerRadius",
    starPoints = "starPoints",
    /**
     * Pro-Editor-Karte (10.10.2026): Fahrt von Ort zu Ort. Jeder Keyframe =
     * „Kamera ist an Ort N“ (Wert = Nummer des Ortes, 0-basiert).
     */
    mapPlace = "mapPlace",
}

export interface AnimationGroupSerialized {
    id: string;
    initialValue: unknown;
    keyframes: Array<KeyframeSerialized>;
    active: boolean;
    type: AnimationType;
    /** Positionspfad als Catmull-Rom-Spline statt Geradenzuegen (Pro-Editor "Glatter Pfad"). */
    smoothPath?: boolean;
}

export interface LayerSerialized {
    id: string;
    name: string;
    type: LayerType;
    from: number;
    to: number;
    visible: boolean;
    pivotPoint?: Vector3Tuple;
    animationsGroups: {
        opacity: AnimationGroupSerialized;
        rotation: AnimationGroupSerialized;
        position: AnimationGroupSerialized;
        scale: AnimationGroupSerialized;
    };
    content: unknown;
    /** Eigenbewegung nach der Uhr (Drehen/Atmen), s. eigenbewegung.ts */
    loops?: LayerEigenbewegung;
}

export interface MediaLayerSerialized extends LayerSerialized {
    type: LayerType.image | LayerType.video | LayerType.lottie;
    isBackground?: boolean;
    content: ImageAssetBackend | VideoAssetBackend;
}

interface VideoLayerSerialized extends MediaLayerSerialized {
    type: LayerType.video;
    content: VideoAssetBackend;
}

interface ImageLayerSerialized extends MediaLayerSerialized {
    type: LayerType.image;
    content: ImageAssetBackend;
}

export function isImageLayer(layer: LayerSerialized): layer is ImageLayerSerialized {
    return layer.type === LayerType.image || layer.type === LayerType.svg;
}

export function isVideoLayer(layer: LayerSerialized): layer is VideoLayerSerialized {
    return layer.type === LayerType.video;
}

export function isLottieLayer(layer: LayerSerialized): boolean {
    return layer.type === LayerType.lottie;
}

export interface TextLayerSerialized extends LayerSerialized {
    type: LayerType.text;
    content: TextContent;
}

export function isTextLayer(layer: LayerSerialized): layer is TextLayerSerialized {
    return layer.type === LayerType.text;
}

export interface PresentationSerialized {
    presentationOptions: PresentationOptionsSerialized;
    id: string;
    slides: Array<SlideSerialized>;
    groups: Array<LayerSerialized>;
    layers: Array<LayerSerialized>;
}

export enum PostType {
    visual = "visual",
}

export interface AssetBackend {
    creator?: {
        id: string;
    };
    key: string;
    id: string;
    mediaType?: string;
    url: string;
    name: string;
    extension: string;
    ppiUrl?: string | undefined;
    ppiExtension?: string | undefined;
}

export interface ImageAssetBackend extends AssetBackend {
    height: number;
    width: number;
    calcHeight: number;
    calcWidth: number;
}

export interface VideoAssetBackend extends AssetBackend {
    duration: number;
    width: number;
    height: number;
    calcWidth: number;
    calcHeight: number;
    chromaKey: {
        a: number;
        b: number;
        g: number;
        r: number;
    };
    dontRestartWhenGoingBack: boolean;
    loop: boolean;
    playbackMode?: 'loop' | 'pingpong' | 'scroll' | 'once';
    muted: boolean;
    tolerance: number;
    transparent: boolean;
    areaX: number;
    areaY: number;
    rotation: number;
    zoom: number;
    autoplay: boolean;
}

export enum ThemeColor {
    light = "light",
    dark = "dark",
}

export enum CustomThemeColor {
    purple = "purple",
    pink = "pink",
    mint = "mint",
    yellow = "yellow",
    navy = "navy",
    olive = "olive",
    graphite = "graphite",
    aubergine = "aubergine",
    black = "black",
    greyMarl = "grey-marl",
    gray = "gray",
    offWhite = "off-white",
    white = "white",
}

export interface PostMetadataSerialized {
    type: PostType;
    title: string;
    shortTitle: string;
    subtitle: string;
    /** Longer social/OG description. Distinct from `subtitle` (the cover line). */
    description?: string;
    credits: string;
    contact?: string;
    sources: string;
    license: string;
    /**
     * KI-Kennzeichnung (KI-Verordnung, Jans Auftrag 01.09.2026): true zeigt im
     * Abbinder unter Credits & Quellen den Hinweis „Dieser Scrolly oder Teile
     * davon wurden mithilfe künstlicher Intelligenz erstellt." Opt-in per
     * Checkbox in beiden Editoren (Credits & Quellen).
     */
    aiDisclosure?: boolean;

    thumbnailResized?: ImageAssetBackend;
    thumbnail?: string;
    /**
     * Cover-Titel (Start-Gate). Das Cover ist kein eigenes Bild, sondern das
     * Bild von Slide 1 mit einem regelbaren "Vorhang" (Overlay) darüber plus
     * diese Titel-Texte/-Optionen.
     */
    /** Kleine Zeile über der Headline (Kicker/Eyebrow). */
    coverTopline?: string;
    /** Text im Start-Button (Default: lokalisiertes "START"). */
    startButtonText?: string;
    /** Anordnung des Titel-Blocks auf dem Cover. */
    coverTextPosition?: "bottom-left" | "bottom-center" | "center";
    /**
     * Intro-Vorhang (Overlay über dem Slide-1-Bild). „Anzeigen" wird über das
     * bestehende `hideIntroOverlay` gesteuert (show = !hideIntroOverlay).
     */
    /** Vorhang unscharf: blurrt das Slide-1-Bild unter dem Vorhang. */
    coverCurtainBlur?: boolean;
    /** Vorhang-Tint: dunkel (Default) oder hell. */
    coverCurtainTheme?: "dark" | "light";
    previewImage?: ImageAssetBackend;
    previewVideo?: VideoAssetBackend;
    titleImage?: ImageAssetBackend;
    startButtonImage?: ImageAssetBackend;
    /**
     * TYPOGRAFIE des Startbildschirms (Pro-Editor-Spiegelung 26.08.2026):
     * Schrift + Größenfaktor für Titel-/Unterzeile des Covers. Auflösung
     * mit Slide-Rückfall: resolveStartTypography (start-typography.ts).
     */
    startHeadlineFont?: string;
    startSublineFont?: string;
    startHeadlineScale?: number;
    startSublineScale?: number;
    hideCloseButton?: boolean;

    theme: ThemeColor;
    /**
     * NAV-BUTTONS (runde Hoch/Runter-Knöpfe unten rechts, ab iPad-Breite im
     * Querformat). Historisch Legacy-Palettenschlüssel (CustomThemeColor);
     * seit 09.08.2026 auch {r,g,b,a} (Pro-Editor-Spiegelung) oder Hex
     * (Template-Editor). Auflösung: resolveNavButtons (nav-buttons.ts).
     */
    iconsColor: CustomThemeColor | { r: number; g: number; b: number; a?: number } | string;
    iconsBgColor: CustomThemeColor | { r: number; g: number; b: number; a?: number } | string;
    /** true = Nav-Buttons generell aus (neu 09.08.2026; ohne Angabe: an). */
    hideNavButtons?: boolean;
    legacy?: boolean;
    hidden?: boolean;
    promotionTag?: boolean;
    hasIframe?: boolean;
    iframeUrl?: string;
    hideIntroOverlay?: boolean;
    /** Startbildschirm-Schalter (Template-Editor, 31.08.2026): true = keine
     *  Titeltexte und kein Vorhang auf dem Cover — nur der START-Knopf
     *  (der bleibt immer, die User-Geste ist Pflicht für iOS-Ton/Autoplay). */
    hideCoverText?: boolean;
    /**
     * Dauerhafte Slide-Kachel-Thumbs des Template-Editors (Jan+Benedikt
     * 01.09.2026): je Slide-Id die als Asset hochgeladene Buehnen-Aufnahme
     * (nur Quadrat-Ratio) + eine Kurzsignatur der Aufnahme, damit ein
     * unveraenderter Re-Capture nicht erneut hochlaedt. Ersetzt die reine
     * localStorage-Loesung (die blieb pro Browser und verwirrte im Team).
     */
    slideThumbs?: Record<string, { url: string; sig: string; inhalt?: string }>;
}

export interface ProfileSerialized {
    avatar: string;
    avatarKey: string;
    id: string;
    firstName: string;
    lastName: string;
    description: string;
    position: string;
}

export enum PublishState {
    published = "published",
    unpublished = "unpublished",
    publishersNotSet = "publishers_not_set",
}

export enum ReviewState {
    created = "created",
    requested = "requested",
    rejected = "rejected",
    approved = "approved",
    reviewersNotSet = "reviewers_not_set",
}

export interface PostSerialized {
    tenants: Array<RealTenantId>;
    id: string;
    slug: string;
    title: string;
    data: PresentationSerialized;
    metadata: PostMetadataSerialized;
    creator?: {
        id: string;
    };
    authors?: Array<ProfileSerialized>;
    description: string;
    reviewers?: Array<ProfileSerialized>;
    publishers?: Array<ProfileSerialized>;
    authorIds: Array<string>;
    reviewerIds: Array<string>;
    publisherIds: Array<string>;
    publishedAt?: string;
    sortingDate?: string | null;
    publishState: PublishState;
    reviewState: ReviewState;
    visibility: PostVisibility;
}

export interface TemplateFrame {
    textSlideIds: string[];
    textLayerIds: string[];
    imageLayerIds: string[];
}

export interface TemplateStructure {
    readonly frames: TemplateFrame[];
}

export interface PostBackend extends PostSerialized {
    createdAt: string;
    updatedAt: string;
    templateStructure: TemplateStructure;
}

export interface PostJson {
    categoryName?: string;
    categoryId?: string;
    subcategoryId?: string;
    subcategoryName?: string;
    data: PostBackend;
}

export enum EPublicTemplateType {
    STANDARD = "STANDARD",
    AI = "AI",
}

export interface PublicTemplate {
    imageSearchTerms: string[][];
    id: number;
    json: PostJson;
    subCategoryName: string | null;
    noOfSlides?: number;
    /** Anzahl austauschbarer Bilder (kuratiert in der Template-Verwaltung,
     *  Jan 11.09.2026) — Badge der Galerie-Kachel und Galerie-Filter. */
    noOfImages?: number | null;
    category?: { id?: string; name?: string } | null;
    /**
     * KURATIERTER Name aus der Template-Verwaltung (DB-Spalte `Template.title`).
     * Der Endpunkt liefert ihn seit jeher — er fehlte nur im Typ, weshalb ALLE
     * Galerie-Ansichten auf `getScrollyTitle(json)` auswichen und damit den
     * ARBEITSNAMEN des Scrollys zeigten, aus dem das Template gebaut wurde
     * (Jans Fund 09.08.2026: „Geburtstags-Template" in der Verwaltung,
     * „Test-Template" in der Galerie). Anzeige immer über `getTemplateTitle`.
     */
    title?: string;
    /**
     * Anlage-Datum. Der Endpunkt liefert es seit jeher, im Typ fehlte es —
     * gebraucht fuer „Neueste zuerst" in der Galerie (Jan 25.09.2026).
     */
    createdAt?: string;
}

export interface CategoryTree {
    category_id: string;
    category_name: string;
    children: CategoryTree[];
}

export interface CustomFonts {
    textFontFamily?: string;
    headlineFontFamily?: string;
    /** Ausdrücklich gewählter Schnitt (s. FontMappingEintrag.schnittGewaehlt), sonst leer. */
    textFontWeight?: number;
    headlineFontWeight?: number;
}

/**
 * Scrolly type for the TemplateEditor context.
 * Maps to the admin panel's UserScrolly with typed JSON.
 */
export interface Scrolly {
    id: number | string;
    publicKey: string;
    json: PostJson;
}
